import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { scheduleApi, ScheduleEntry } from '../../lib/api';
import { RootState } from '../../store/store';
import { Clock, Plus, Trash2, Save, AlertCircle } from 'lucide-react';

const daysOfWeek = [
  { value: 'MONDAY', label: 'Monday' },
  { value: 'TUESDAY', label: 'Tuesday' },
  { value: 'WEDNESDAY', label: 'Wednesday' },
  { value: 'THURSDAY', label: 'Thursday' },
  { value: 'FRIDAY', label: 'Friday' },
  { value: 'SATURDAY', label: 'Saturday' },
  { value: 'SUNDAY', label: 'Sunday' },
] as const;

const DoctorSchedulePage: React.FC = () => {
  const [schedule, setSchedule] = useState<ScheduleEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const { user } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect if not a doctor
    if (!user || user.role !== 'DOCTOR') {
      toast.error('Access denied. Only doctors can view their schedule.');
      navigate('/dashboard');
      return;
    }

    fetchSchedule();
  }, [user, navigate]);

  const fetchSchedule = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await scheduleApi.getSchedule('me');
      console.log('Schedule response:', response.data);
      if (response.data.success && response.data.data) {
        // The response.data.data is the schedule array
        setSchedule(response.data.data);
      } else {
        console.log('No schedule data, setting empty array');
        setSchedule([]);
      }
    } catch (error: any) {
      console.error('Error fetching schedule:', error);
      setError(error.response?.data?.message || 'Failed to load schedule');
      toast.error('Failed to load schedule');
      setSchedule([]);
    } finally {
      setLoading(false);
    }
  };

  const addScheduleEntry = (day: string) => {
    const newEntry: ScheduleEntry = {
      dayOfWeek: day,
      startTime: '09:00',
      endTime: '17:00',
      isAvailable: true
    };
    setSchedule(prevSchedule => [...prevSchedule, newEntry]);
  };

  const updateScheduleEntry = (index: number, field: keyof ScheduleEntry, value: any) => {
    setSchedule(prevSchedule => {
      const updatedSchedule = [...prevSchedule];
      updatedSchedule[index] = { ...updatedSchedule[index], [field]: value };
      return updatedSchedule;
    });
  };

  const removeScheduleEntry = (index: number) => {
    setSchedule(prevSchedule => prevSchedule.filter((_, i) => i !== index));
  };

  const validateSchedule = () => {
    if (!Array.isArray(schedule)) {
      return ['Invalid schedule format'];
    }

    const errors: string[] = [];
    
    schedule.forEach((entry, index) => {
      if (entry.startTime >= entry.endTime) {
        errors.push(`Time slot ${index + 1}: Start time must be before end time`);
      }
    });

    // Check for overlapping times on the same day
    const dayGroups = schedule.reduce((acc, entry, index) => {
      if (!acc[entry.dayOfWeek]) {
        acc[entry.dayOfWeek] = [];
      }
      acc[entry.dayOfWeek].push({ ...entry, index });
      return acc;
    }, {} as Record<string, (ScheduleEntry & { index: number })[]>);

    Object.entries(dayGroups).forEach(([day, entries]) => {
      entries.sort((a, b) => a.startTime.localeCompare(b.startTime));
      
      for (let i = 0; i < entries.length - 1; i++) {
        const current = entries[i];
        const next = entries[i + 1];
        
        if (current.endTime > next.startTime) {
          errors.push(`${day}: Time slots ${current.index + 1} and ${next.index + 1} overlap`);
        }
      }
    });

    return errors;
  };

  const handleSave = async () => {
    try {
      const validationErrors = validateSchedule();
      if (validationErrors.length > 0) {
        toast.error(validationErrors[0]);
        return;
      }

      setSaving(true);
      setError(null);
      
      console.log('Saving schedule:', schedule);
      const response = await scheduleApi.updateSchedule('me', schedule);
      console.log('Save response:', response.data);
      
      if (response.data.success && response.data.data) {
        // Update local state with the returned schedule
        const updatedSchedule = Array.isArray(response.data.data) ? response.data.data : [];
        setSchedule(updatedSchedule);
        toast.success('Schedule updated successfully');
      } else {
        throw new Error(response.data.message || 'Failed to update schedule');
      }
    } catch (error: any) {
      console.error('Error saving schedule:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save schedule';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getDaySchedules = (day: string) => {
    if (!Array.isArray(schedule)) return [];
    return schedule
      .filter(entry => entry.dayOfWeek === day)
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading schedule...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-lg p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Schedule Management</h1>
            <p className="text-sm text-gray-600 mt-1">
              Manage your weekly availability schedule
            </p>
          </div>
          <div className="flex gap-4 w-full sm:w-auto">
            <button
              onClick={handleSave}
              className="btn-primary w-full sm:w-auto"
              disabled={saving}
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        )}

        {/* Schedule Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
          {daysOfWeek.map(day => (
            <div 
              key={day.value} 
              className={`bg-gray-50 rounded-lg p-4 border-2 transition-colors ${
                selectedDay === day.value 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-blue-300'
              }`}
              onClick={() => setSelectedDay(day.value)}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">{day.label}</h3>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    addScheduleEntry(day.value);
                  }}
                  className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  disabled={saving}
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <div className="space-y-3">
                {getDaySchedules(day.value).map((entry, index) => (
                  <div key={index} className="bg-white rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-600">Time Slot {index + 1}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeScheduleEntry(schedule.indexOf(entry));
                        }}
                        className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                        disabled={saving}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-gray-500 flex-shrink-0" />
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 flex-1">
                          <div className="w-full sm:w-1/2">
                            <label className="block text-xs font-medium text-gray-500 mb-1">Start Time</label>
                            <input
                              type="time"
                              value={entry.startTime}
                              onChange={(e) => updateScheduleEntry(schedule.indexOf(entry), 'startTime', e.target.value)}
                              className="input text-sm w-full"
                              disabled={saving}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <div className="w-full sm:w-1/2">
                            <label className="block text-xs font-medium text-gray-500 mb-1">End Time</label>
                            <input
                              type="time"
                              value={entry.endTime}
                              onChange={(e) => updateScheduleEntry(schedule.indexOf(entry), 'endTime', e.target.value)}
                              className="input text-sm w-full"
                              disabled={saving}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                      </div>
                      <label className="flex items-center mt-2" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={entry.isAvailable}
                          onChange={(e) => updateScheduleEntry(schedule.indexOf(entry), 'isAvailable', e.target.checked)}
                          className="sr-only"
                          disabled={saving}
                        />
                        <div className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          entry.isAvailable ? 'bg-blue-600' : 'bg-gray-200'
                        }`}>
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              entry.isAvailable ? 'translate-x-5' : 'translate-x-1'
                            }`}
                          />
                        </div>
                        <span className="ml-2 text-xs text-gray-700">
                          {entry.isAvailable ? 'Available' : 'Unavailable'}
                        </span>
                      </label>
                    </div>
                  </div>
                ))}
                {getDaySchedules(day.value).length === 0 && (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    No time slots added
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DoctorSchedulePage; 